import { isLeft } from "fp-ts/lib/Either";
import { PathReporter } from "io-ts/lib/PathReporter";

import {
  EmbedOptions,
  LANGUAGE_CODE,
  PLAYER_DEFAULT_VALUES
} from "../constants";
import { getUrlOrHashParam, parseNumericParam } from "../helpers/urlParams";
import {
  DeprecatedPlayerEmbedParams,
  Language,
  PlayerEmbedParams
} from "../types/common";

interface PlayerElement extends HTMLDivElement {
  ylePlayerDebug: () => unknown;
}

const deprecatedEmbedParamRemap = {
  audioDescription: "description",
  audioTitle: "title",
  noRecommendations: "recommendations",
  noSharing: "sharing",
  showAudioPreviewButton: "playButton",
  showBackButton: "backButton",
  showChatButton: "chatButton",
  showPlayButton: "playButton",
  showPlayerUi: "playerUi",
  showReplayButton: "replayButton",
  showSpinner: "spinner",
  showUnmuteButton: "unmuteButton",
  videoDescription: "description",
  videoTitle: "title",
  ylePlayer: "ylePlayer"
} satisfies Record<keyof DeprecatedPlayerEmbedParams, keyof PlayerEmbedParams>;

const backgroundVideoParams = {
  analytics: false,
  autoFocus: false,
  autoMute: true,
  autoPlayNextEpisode: false,
  backButton: false,
  chatButton: false,
  chromecast: false,
  description: false,
  logo: false,
  loop: true,
  optimizeStartLevel: true,
  playbackHistory: false,
  playButton: false,
  playFullScreen: false,
  recommendations: false,
  replayButton: false,
  sharing: false,
  smallPlayButton: false,
  subtitles: false,
  timeLeft: false,
  title: false,
  unmuteButton: false
};

const invalidParameterError = (key: string, error: string | string[]) => {
  console.warn(`Embed parameter validation error for key "${key}": ${error}`);
};

const allEmbedParamKeys = EmbedOptions.types.reduce<string[]>(
  (arr, type) => [...arr, ...Object.keys(type.props)],
  ["id", "src", ...Object.keys(deprecatedEmbedParamRemap)]
);
const parseDataAttributes = (element: Element): Partial<PlayerEmbedParams> =>
  allEmbedParamKeys.reduce<Partial<PlayerEmbedParams>>((obj, key) => {
    const elementAttribute = element.getAttribute(`data-${key}`);
    if (elementAttribute) {
      switch (elementAttribute) {
        case "true":
          obj[key] = true;
          break;
        case "false":
          obj[key] = false;
          break;
        default:
          obj[key] = elementAttribute;
          break;
      }
    }
    return obj;
  }, {});

const isLanguageCode = (value: string): value is Language =>
  Object.values<string>(LANGUAGE_CODE).includes(value);

const parseLocaleDataAttribute = (
  element: Element,
  dataAttributes: Partial<PlayerEmbedParams>
): { language: Language } | undefined => {
  const locale = element.getAttribute("data-locale");

  if (locale && isLanguageCode(locale) && !dataAttributes.language) {
    return { language: locale };
  }
};

// Embed params are case-insensitive. Correct casing to match EmbedOptions -type.
const correctParamsCasing = (
  params: Partial<PlayerEmbedParams> = {}
): Partial<PlayerEmbedParams> => {
  return Object.entries(params).reduce<Partial<PlayerEmbedParams>>(
    (obj, [embedParamKey, value]) => {
      const correctKey = allEmbedParamKeys.find(
        key => key.toLowerCase() === embedParamKey.toLowerCase()
      );
      obj[correctKey || embedParamKey] = value;
      return obj;
    },
    {}
  );
};

const parseNumericParams = (
  params: Partial<PlayerEmbedParams> = {},
  dataAttributes: Partial<PlayerEmbedParams> = {}
): Partial<PlayerEmbedParams> => {
  return {
    playbackRate: parseNumericParam(params?.playbackRate, 2),
    seek: parseNumericParam(
      params?.seek ?? getUrlOrHashParam(window, "seek") ?? dataAttributes?.seek
    ),
    srcDuration: parseNumericParam(
      params?.srcDuration ?? dataAttributes?.srcDuration
    )
  };
};

const getBackgroundVideoParams = (
  params: PlayerEmbedParams,
  userGivenParams: Partial<PlayerEmbedParams>
): Partial<PlayerEmbedParams> => {
  if (params.backgroundVideo) {
    const changedParams = Object.entries(backgroundVideoParams).reduce<
      string[]
    >((arr, [key, value]) => {
      if (
        userGivenParams[key] !== undefined &&
        userGivenParams[key] !== value
      ) {
        arr.push(key);
      }
      return arr;
    }, []);
    if (changedParams.length) {
      console.info(
        `Following params were overridden by backgroundVideo -parameter: ${changedParams.join(
          ", "
        )}`
      );
    }

    return backgroundVideoParams;
  }
  return {};
};

const getAutoplayParameter = (
  params: Partial<PlayerEmbedParams>
): Pick<PlayerEmbedParams, "autoplay"> => {
  const prefersReducedMotion = !!window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  if (prefersReducedMotion) {
    if (params.autoplay) {
      console.info(
        "ylePlayer: autoplay was overridden by (prefers-reduced-motion: reduce)."
      );
    }
    return { autoplay: false };
  }

  const autoplayDefaultValue = !!params.backgroundVideo; // For background videos, autoplay is true by default
  return { autoplay: params.autoplay ?? autoplayDefaultValue };
};

export const createValidEmbedParamsObject = (
  params: Partial<PlayerEmbedParams>,
  defaults: PlayerEmbedParams = PLAYER_DEFAULT_VALUES
): PlayerEmbedParams => {
  // Show error for param names that don't exist in PlayerEmbedParams
  const notExistingParams = Object.keys(params).filter(
    key => !allEmbedParamKeys.includes(key)
  );
  notExistingParams.forEach(key => {
    invalidParameterError(key, "Invalid parameter name.");
  });

  // Validate params against io-ts type
  const decoded = EmbedOptions.decode({ ...defaults, ...params });
  const invalidParams = isLeft(decoded)
    ? decoded.left.map(v => {
        const { actual, key, type } = v.context[2];
        if (typeof actual !== "undefined") {
          invalidParameterError(key, PathReporter.report(type.decode(actual)));
        }
        return key;
      })
    : [];

  const allInvalidParams = [...notExistingParams, ...invalidParams];

  // Remove invalid values
  const playerEmbedParams = Object.entries(params).reduce<PlayerEmbedParams>(
    (obj, [key, value]) => {
      if (value !== undefined && !allInvalidParams.includes(key)) {
        obj[key] = value;
      }

      return obj;
    },
    { ...defaults }
  );

  // Map deprecated params
  Object.entries(deprecatedEmbedParamRemap).forEach(
    ([deprecatedParam, newParam]) => {
      if (
        typeof playerEmbedParams[deprecatedParam] !== "undefined" &&
        typeof params[newParam] === "undefined"
      ) {
        console.info(
          `Embed param "${deprecatedParam}" is deprecated. Use "${newParam}" instead.`
        );
        playerEmbedParams[newParam] = deprecatedParam.startsWith("no")
          ? !playerEmbedParams[deprecatedParam]
          : playerEmbedParams[deprecatedParam];
      }
      delete playerEmbedParams[deprecatedParam];
    }
  );

  return {
    ...playerEmbedParams,
    ...getBackgroundVideoParams(playerEmbedParams, params),
    ...getAutoplayParameter(params)
  };
};

export const getPlayerEmbedParams = (
  element: Element,
  props?: Partial<PlayerEmbedParams>
): PlayerEmbedParams => {
  const caseCorrectedEmbedParams = correctParamsCasing(props);
  const dataAttributes = parseDataAttributes(element);
  const embedParameters = createValidEmbedParamsObject({
    ...dataAttributes,
    ...caseCorrectedEmbedParams,
    ...parseNumericParams(caseCorrectedEmbedParams, dataAttributes),
    ...parseLocaleDataAttribute(element, dataAttributes)
  });

  (element as PlayerElement).ylePlayerDebug = () => ({
    dataAttributes,
    embedParameters,
    renderProps: props
  });

  return embedParameters;
};
